import { InvestmentEntity } from 'types/investment-entity';
import { Link } from 'types/layout';
import { marketingLinks } from '@components/header/navigation/links';
import { NavigationLink } from 'types/header';
import { User } from 'types/user';

export default class AuthenticatedNav {
	public links!: Array<NavigationLink>;

	public firstInvestmentDate!: string;

	public fullName!: string;

	public multipleInvestmentEntities!: boolean;

	public inviteLink!: Link | null;

	private setFirstInvestmentDate(user: User): void {
		if (user && user.entityDetails) {
			this.firstInvestmentDate = user.entityDetails.firstInvestmentDate;
		}
	}

	private setFullName(user: User): void {
		if (user && user.firstName && user.lastName) {
			this.fullName = user.firstName + ' ' + user.lastName;
		} else if (user && user.firstName) {
			this.fullName = user.firstName;
		} else {
			this.fullName = 'Account';
		}
	}

	private setMultipleInvestmentEntities(user: User): void {
		if (user) {
			this.multipleInvestmentEntities = user.entityDetails.investmentEntities.length > 1;
		} else {
			this.multipleInvestmentEntities = false;
		}
	}

	private setInviteLink(selectedInvestmentEntity: InvestmentEntity): void {
		if (
			selectedInvestmentEntity.userReferralEligibilityStatus &&
			selectedInvestmentEntity.userReferralEligibilityStatus != 'INELIGIBLE'
		) {
			this.inviteLink = {
				href: '/invitation-program/landing?cta=Invite a Friend CTA',
				text: 'Invite a Friend'
			};
		} else {
			this.inviteLink = null;
		}
	}

	private setLinks(user: User, selectedInvestmentEntity: InvestmentEntity): void {
		if (selectedInvestmentEntity.isReitInvestor) {
			this.links = [
				{
					text: 'Overview',
					router: 'account-overview'
				},
				{
					text: 'Performance',
					router: 'account-performance'
				},
				{
					text: 'Portfolio',
					router: 'account-portfolio'
				},
				{
					text: 'Transactions',
					router: 'account-transactions'
				},
				{
					dropdown: 'More',
					links: this.constructMoreDropdownLinks(user, selectedInvestmentEntity)
				}
			];
		} else {
			const newsfeedLink: NavigationLink = {
				text: 'Newsfeed',
				router: 'newsfeed-public'
			};
			this.links = [...marketingLinks, newsfeedLink];
		}
	}

	private constructMoreDropdownLinks(user: User, selectedInvestmentEntity: InvestmentEntity): Array<Link> {
		const subMenuLinks = [
			{
				text: 'Newsfeed',
				router: 'newsfeed'
			},
			{
				text: 'Help center',
				href: '/help'
			},
			{
				text: 'Documents',
				router: 'account-documents'
			}
		];

		if (selectedInvestmentEntity.showTaxCenter) {
			subMenuLinks.push({
				text: 'Tax Center',
				router: 'tax-center'
			});
		}

		if (selectedInvestmentEntity.eligibleForInvestorGoal) {
			subMenuLinks.push({
				text: 'Goal',
				router: selectedInvestmentEntity.investorGoalExists ? 'investment-goal-edit' : 'investment-goal'
			});
		}

		subMenuLinks.push({
			text: 'Features',
			href: '/account/features'
		});

		if (selectedInvestmentEntity.showBrowseMenuItem) {
			subMenuLinks.push({
				text: 'Browse Investments',
				href: '/offerings'
			});
		}

		if (selectedInvestmentEntity.currentRiaPlanName === '') {
			subMenuLinks.push({
				text: 'Upgrade to Fundrise 2.0',
				href: '/existing/landing'
			});
		}

		return subMenuLinks;
	}

	public constructor(user: User | null, selectedInvestmentEntity: InvestmentEntity | null) {
		if (!user || !selectedInvestmentEntity) {
			return;
		}
		this.setMultipleInvestmentEntities(user);
		this.setFirstInvestmentDate(user);
		this.setFullName(user);
		this.setInviteLink(selectedInvestmentEntity);
		this.setLinks(user, selectedInvestmentEntity);
	}
}
